import { useMutation } from '@tanstack/react-query';
import { removeConditionFromAnotherClinic } from '../../../../apis/clinics/requests';
import { ClinicFormProps } from '../../CreateClinic/types';
import { ClinicCondition } from '../../types';
import { useSnackbar } from 'notistack';
import useUpdateClinic from './useUpdateClinic';
import { ClinicWithConditionsAndPromo } from '../../types';

interface DeleteConditionParams {
  conditionId: string;
  currentClinicId: string;
}

interface UseDeleteConditionProps {
  form: ClinicFormProps['form'];
  clinicId: string;
}

export function useDeleteCondition({ form, clinicId }: UseDeleteConditionProps) {
  const conditions = form.watch('clinic_conditions') as ClinicCondition[];
  const { enqueueSnackbar } = useSnackbar();
  const { updateClinic } = useUpdateClinic();

  const mutation = useMutation({
    mutationFn: async ({ conditionId }: DeleteConditionParams) => {
      const condition = conditions.find(condition => condition._id === conditionId);
      if (!condition) return;

      if (condition?.clinic_id !== clinicId) {
        await removeConditionFromAnotherClinic(conditionId, clinicId);
      } else {
        const updatedConditions = conditions.filter(condition => condition._id !== conditionId);
        await updateClinic([clinicId, { clinic_conditions: updatedConditions } as Partial<ClinicWithConditionsAndPromo>]);
      }

      const updatedConditions = conditions.filter(condition => condition._id !== conditionId);
      form.setValue('clinic_conditions', updatedConditions, { shouldDirty: true });
    },
    onSuccess: () => {
      enqueueSnackbar('Condition deleted successfully', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete condition', { variant: 'error' });
    }
  });

  return {
    deleteCondition: (conditionId: string) => mutation.mutate({ conditionId, currentClinicId: clinicId }),
    isDeleting: mutation.isLoading
  };
}

export default useDeleteCondition;
